import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Chip,
  Divider,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Circle as CircleIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import ProfileSwitcher from './ProfileSwitcher';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'customer' | 'agent' | 'admin';
  avatar?: string;
}

interface SystemStatus {
  database: boolean;
  mcpServers: boolean;
  aiAgent: boolean;
  llmStatus: boolean;
}

interface HeaderProps {
  user: User;
  onLogout: () => void;
  onProfileSwitch?: (newRole: 'customer' | 'agent' | 'admin') => void;
  systemStatus: SystemStatus;
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({
  user,
  onLogout,
  onProfileSwitch,
  systemStatus,
  onMenuClick,
}) => {
  const { logout: auth0Logout } = useAuth0();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    onLogout();
    auth0Logout({ 
      logoutParams: { 
        returnTo: 'https://procogia.com/solutions/' 
      }
    });
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return '#1e212a';
      case 'agent':
        return '#95c100';
      case 'customer':
        return '#1fb8cc';
      default:
        return '#757575';
    }
  };

  const getSystemHealthStatus = () => {
    const connectedServices = [
      systemStatus.database,
      systemStatus.mcpServers,
      systemStatus.aiAgent,
    ].filter(Boolean).length;
    
    const totalServices = 3;
    
    if (connectedServices === totalServices) {
      return { color: '#95c100', text: 'All Systems Operational' };
    } else if (connectedServices > 0) {
      return { color: '#95c100', text: `${connectedServices}/${totalServices} Services Online` };
    } else {
      return { color: '#e74c3c', text: 'System Offline' };
    }
  };

  const systemHealth = getSystemHealthStatus();

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, #1fb8cc 0%, #95c100 100%)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
        {/* Left side - Menu and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1 }}>
              ProCogia Telecom AI
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1 }}>
              Intelligent Customer Service Platform
            </Typography>
          </Box>
        </Box>

        {/* Center - System Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircleIcon
            sx={{
              fontSize: 12,
              color: systemHealth.color,
              filter: 'brightness(1.2)',
            }}
          />
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            {systemHealth.text}
          </Typography>
        </Box>

        {/* Right side - Profile Switcher and User Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Profile Switcher */}
          {onProfileSwitch && (
            <ProfileSwitcher
              currentUser={user}
              onProfileSwitch={onProfileSwitch}
            />
          )}

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton color="inherit">
              <NotificationsIcon />
            </IconButton>
          </Tooltip>

          {/* User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              src={user.avatar}
              sx={{
                width: 36,
                height: 36,
                cursor: 'pointer',
                border: '2px solid rgba(255, 255, 255, 0.3)',
              }}
              onClick={handleMenuOpen}
            />
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1 }}>
                {user.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  label={user.role.toUpperCase()}
                  size="small"
                  sx={{
                    backgroundColor: getRoleColor(user.role),
                    color: 'white',
                    fontSize: '0.7rem',
                    height: 16,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Demo
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* User Menu Dropdown */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                borderRadius: 2,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              },
            }}
          >
            {/* User Info */}
            <Box sx={{ p: 2, pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Avatar src={user.avatar} sx={{ width: 40, height: 40 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {user.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {user.email}
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={`${user.role.toUpperCase()} DEMO`}
                size="small"
                sx={{
                  backgroundColor: getRoleColor(user.role),
                  color: 'white',
                  fontSize: '0.7rem',
                }}
              />
            </Box>

            <Divider />

            {/* Profile Menu Item */}
            <MenuItem onClick={handleMenuClose}>
              <AccountIcon sx={{ mr: 1, fontSize: 20 }} />
              Profile Settings
            </MenuItem>

            <Divider />

            {/* Logout */}
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1, fontSize: 20, color: '#1fb8cc' }} />
              <Typography sx={{ color: '#1fb8cc' }}>
                Sign Out
              </Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header; 